import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { IngredientCategoryListRow } from "@/modules/inventory/domain/types";
import { confirm } from "@/shared/lib/confirm";
import { RowActions } from "@/shared/ui/RowActions";
import { DataTable, createSortableColumn, TanstackReactTable } from "@/shared/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { EditIcon, GripVerticalIcon, PowerOffIcon } from "lucide-react";

type IngredientCategoriesTableProps = {
  data: IngredientCategoryListRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (row: IngredientCategoryListRow) => void;
  onDeactivate: (id: string) => void;
  reorderable?: boolean;
  onReorder?: (fromId: string, toId: string) => void;
};

export function IngredientCategoriesTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onDeactivate,
  reorderable,
  onReorder,
}: IngredientCategoriesTableProps) {
  const { has } = usePermissions();

  const columns: TanstackReactTable.ColumnDef<IngredientCategoryListRow>[] = [
    ...(reorderable
      ? [
          {
            id: "drag",
            header: "",
            enableSorting: false,
            size: 36,
            cell: ({ row }: { row: { original: IngredientCategoryListRow } }) => (
              <button
                type="button"
                draggable
                className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing inline-flex touch-none p-1"
                aria-label="ລາກເພື່ອຈັດລຳດັບ"
                onDragStart={(e) => {
                  e.dataTransfer.setData("text/plain", row.original.id);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const fromId = e.dataTransfer.getData("text/plain");
                  if (fromId && fromId !== row.original.id) {
                    onReorder?.(fromId, row.original.id);
                  }
                }}
              >
                <GripVerticalIcon className="size-4" />
              </button>
            ),
          } as TanstackReactTable.ColumnDef<IngredientCategoryListRow>,
        ]
      : []),
    createSortableColumn<IngredientCategoryListRow>("name", "ຊື່ຫມວດ", {
      size: 180,
    }),
    createSortableColumn<IngredientCategoryListRow>("code", "ລະຫັດ", {
      size: 100,
    }),
    createSortableColumn<IngredientCategoryListRow>("sortOrder", "ລຳດັບ", {
      size: 80,
    }),
    {
      id: "isActive",
      header: "ສະຖານະ",
      cell: ({ row }) =>
        row.original.isActive ? (
          <Badge variant="default">ເປີດ</Badge>
        ) : (
          <Badge variant="secondary">ປິດ</Badge>
        ),
      size: 80,
    },
    {
      size: 120,
      id: "actions",
      cell: ({ row }) => {
        const id = row.original.id;
        const actions: {
          label: string;
          icon?: React.ReactNode;
          variant?: "destructive";
          onClick: () => void;
        }[] = [];

        if (has("inventory:update")) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });
          if (row.original.isActive) {
            actions.push({
              label: "ປິດໃຊ້",
              variant: "destructive",
              icon: <PowerOffIcon className="h-4 w-4" />,
              onClick: async () => {
                const ok = await confirm({
                  title: "ປິດໃຊ້ຫມວດ",
                  description:
                    "ຫມວດນີ້ຈະບໍ່ໃຊ້ມອບຫມາຍໃໝ່ — ວັດຖຸດິບທີ່ຜູ້ຢູ່ຍັງຄົງຫມວດເດີມ",
                  actionText: "ປິດໃຊ້",
                  ActionProps: { variant: "destructive" },
                });
                if (ok) onDeactivate(id);
              },
            });
          }
        }

        return <RowActions actions={actions} maxInline={3} />;
      },
    },
  ];

  return (
    <DataTable<IngredientCategoryListRow, unknown>
      noDataMessage="ບໍ່ພົບຫມວດວັດຖຸດິບ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
